import { api } from "@/lib/api/client";
import type {
  InvitationListFilters,
  InvitationListResponse,
  InvitationStats,
  OnboardingInvitation,
  OnboardingProfileType,
} from "./types";

function buildParams(filters: InvitationListFilters): Record<string, string> {
  const out: Record<string, string> = {};
  if (filters.status) out.status = filters.status;
  if (filters.profileType) out.profileType = filters.profileType;
  if (filters.q) out.q = filters.q;
  if (filters.page) out.page = String(filters.page);
  if (filters.pageSize) out.pageSize = String(filters.pageSize);
  return out;
}

// Pipeline unificado: las invitaciones se consumen vía las rutas de Seguimiento
// (RBAC v2: SEGUIMIENTO_VIEW/MANAGE), no las legacy /admin/* (allowlist). El
// mismo controller atiende ambas; estas rutas además linkean al CRM y, al
// invitar desde un prospecto, aceptan `crmProspectId`.
const BASE = "/api/nexus/seguimiento/invitations";

export const invitationsApi = {
  stats: async (): Promise<InvitationStats> => {
    const { data } = await api.get<InvitationStats>(`${BASE}/stats`);
    return data;
  },
  list: async (filters: InvitationListFilters): Promise<InvitationListResponse> => {
    const { data } = await api.get<InvitationListResponse>(BASE, {
      params: buildParams(filters),
    });
    return data;
  },
  create: async (
    profileType: OnboardingProfileType,
    email: string,
    crmProspectId?: number,
  ): Promise<OnboardingInvitation> => {
    const { data } = await api.post<{ invitation: OnboardingInvitation }>(BASE, {
      profileType,
      email,
      ...(crmProspectId != null ? { crmProspectId } : {}),
    });
    return data.invitation;
  },
  /**
   * Invita a UNO o VARIOS perfiles a la vez (sep-2026). El backend crea una
   * invitación por perfil y manda un solo correo; su link activa todos los
   * perfiles pendientes de esa persona. `skippedProfiles` = los que la cuenta
   * ya tenía activos (no se invitan).
   */
  createMany: async (
    profileTypes: OnboardingProfileType[],
    email: string,
  ): Promise<{ invitations: OnboardingInvitation[]; skippedProfiles: OnboardingProfileType[] }> => {
    const { data } = await api.post<{
      invitation: OnboardingInvitation;
      invitations?: OnboardingInvitation[];
      skippedProfiles?: OnboardingProfileType[];
    }>(BASE, { profileTypes, email });
    return {
      invitations: data.invitations ?? [data.invitation],
      skippedProfiles: data.skippedProfiles ?? [],
    };
  },
  resend: async (id: number, reminder = false): Promise<OnboardingInvitation> => {
    const { data } = await api.post<{ invitation: OnboardingInvitation }>(
      `${BASE}/${id}/resend`,
      reminder ? { reminder: true } : {},
    );
    return data.invitation;
  },
  revoke: async (id: number): Promise<OnboardingInvitation> => {
    const { data } = await api.post<{ invitation: OnboardingInvitation }>(
      `${BASE}/${id}/revoke`,
    );
    return data.invitation;
  },
};
